import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react"
import { useTranslation } from "react-i18next"
import { SendIcon, SquareIcon } from "lucide-react"

import { Button } from "@f2e/ui/components/base"
import {
  AutosizeTextarea,
  AutosizeTextAreaRef,
} from "@f2e/ui/components/base-plus"

import DragDropOverlay from "@/components/Conversation/DragDropOverlay"
import { useDragAndDrop, useToast } from "@/hooks"
import { useUploadCopilotFileApi } from "@/hooks/api/copilot"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import CopilotFileUpload from "./CopilotFileUpload"
import type { CopilotFile } from "./types"
import { processAndUploadCopilotFiles } from "./utils"

interface CopilotInputProps {
  value: string
  placeholder?: string
  disabled?: boolean
  isSending?: boolean
  onValueChange: (value: string) => void
  onSend?: (message: string, files?: CopilotFile[]) => void
  onCancel?: () => void
}

const CopilotInput = forwardRef<AutosizeTextAreaRef, CopilotInputProps>(
  (
    {
      value,
      placeholder,
      disabled = false,
      isSending = false,
      onValueChange,
      onSend,
      onCancel,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { alertToast } = useToast()

    const [files, setFiles] = useState<CopilotFile[]>([])
    const textareaRef = useRef<AutosizeTextAreaRef | null>(null)
    const {
      error: dndUploadError,
      data: dndUploadResp,
      fetchFn: dndUpload,
    } = useUploadCopilotFileApi()
    const { isDragging, dragHandlers } = useDragAndDrop({
      onFilesDropped: async (droppedFiles: File[]) => {
        await processAndUploadCopilotFiles({
          files: droppedFiles,
          t,
          alertToast,
          upload: dndUpload,
          onAdd: (newFile: CopilotFile) =>
            setFiles((prev) => [...prev, newFile]),
        })
      },
      logPrefix: "CopilotSurface",
    })

    // Expose the textarea ref to parent components
    useImperativeHandle(ref, () => textareaRef.current!, [])

    const canSend = value.trim().length > 0 && !disabled && !isSending

    // Show error for DnD upload via toast using hook response
    useEffect(() => {
      if (!dndUploadError) return
      alertToast({
        title: t("common.fileUpload.error.title"),
        description: getUserFacingApiErrorMessage(
          dndUploadError,
          t,
          dndUploadResp?.message || t("common.fileUpload.error.defaultMessage")
        ),
      })
    }, [dndUploadError, dndUploadResp, alertToast, t])

    const handleSend = () => {
      if (!canSend) return

      onSend?.(value.trim(), files)
      setFiles([])
      // Keep focus on the textarea after sending
      textareaRef.current?.textArea?.focus({ preventScroll: true })
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.shiftKey && e.key === "Enter") return
      if (e.key === "Enter" && !e.nativeEvent.isComposing) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div
        className="border-t border-border bg-background p-4 relative"
        {...dragHandlers}
      >
        <DragDropOverlay
          isVisible={isDragging}
          minimal
          className="rounded-3xl inset-1 border-primary/40 bg-transparent"
        />
        {/* Big Container Box */}
        <div className="flex flex-col rounded-lg border border-border bg-background p-1 focus-within:ring-1 focus-within:ring-ring transition-colors duration-200">
          {/* Top Section: AutosizeTextarea (no border) */}
          {/* 
            Height strategy:
            - No fixed h-[px] class to avoid conflicts with AutosizeTextarea
            - minHeight/maxHeight props let AutosizeTextarea handle sizing
            - Removed the manual useEffect that was setting style.height (conflicted with autosize)
          */}
          <AutosizeTextarea
            ref={textareaRef}
            className={`p-2 text-sm resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            placeholder={placeholder ?? t("common.copilot.input.placeholder")}
            minHeight={36}
            maxHeight={144}
            value={value}
            readOnly={disabled}
            aria-disabled={disabled}
            onChange={(e) => onValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          {/* Bottom Section: Action Buttons */}
          <div className="flex items-end justify-between mt-2">
            {/* Left Side: Function Buttons */}
            <div className="flex items-end gap-2">
              <CopilotFileUpload
                files={files}
                onFilesChange={setFiles}
                disabled={disabled || isSending}
              />
            </div>

            {/* Right Side: Send/Cancel Button */}
            <div className="flex items-end gap-2">
              {isSending ? (
                <Button
                  size="icon"
                  variant="default"
                  className="h-8 w-8 rounded-full p-0 shrink-0 animate-pulse"
                  onClick={onCancel}
                >
                  <SquareIcon className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant="default"
                  className="h-8 w-8 rounded-full p-0 shrink-0"
                  disabled={!canSend}
                  onClick={handleSend}
                >
                  <SendIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

CopilotInput.displayName = "CopilotInput"

export default CopilotInput
